import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { QuoteCard } from "@/components/QuoteCard";
import { TagChip } from "@/components/TagChip";
import { AuthErrorPrompt } from "@/components/AuthErrorPrompt";
import { DiscoverLoadingState } from "@/components/DiscoverLoadingState";
import { useAuthEnabled } from "@/lib/authQuery";
import {
  useGetQuotesRecommendations,
  useGetQuotesFavorites,
  useGetQuotes,
  useGetTags,
  useGetProfileQuoteClusters,
  getGetProfileQuoteClustersQueryKey,
  getGetQuotesRecommendationsQueryKey,
  getGetQuotesFavoritesQueryKey,
  getGetQuotesQueryKey,
  getGetTagsQueryKey,
  type RecommendedQuote,
  type QuoteWithTags,
} from "@workspace/api-client-react";

const TOP_TAG_COUNT = 10;
const LIBRARY_PAGE_SIZE = 40;

function toCardQuote(q: QuoteWithTags) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

function toDiscoverCardQuote(q: RecommendedQuote) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
    reasonSnippet: q.reasonSnippet ?? null,
    score: q.score ?? null,
  };
}

function isAuthError(error: unknown): boolean {
  return (error as any)?.status === 401 || (error as any)?.status === 403;
}

function matchesSearch(q: { text: string; author: string | null }, term: string): boolean {
  const lower = term.toLowerCase();
  return (
    q.text.toLowerCase().includes(lower) ||
    (q.author?.toLowerCase().includes(lower) ?? false)
  );
}

function TasteSnapshot({ clusters }: { clusters: { label: string; percentage: number }[] }) {
  const top = clusters.filter((c) => c.percentage > 0).slice(0, 3);
  if (top.length === 0) return null;
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-400">
        Your taste profile
      </p>
      <div className="space-y-2.5">
        {top.map((c) => (
          <div key={c.label}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium text-stone-700">{c.label}</span>
              <span className="text-xs text-stone-400">{c.percentage}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
              <div
                className="h-full rounded-full bg-stone-800 transition-all duration-700"
                style={{ width: `${c.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscoverEmptyState() {
  const [, navigate] = useLocation();
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100 text-2xl">
        ✦
      </div>
      <p className="mb-2 text-lg font-semibold text-stone-900">Teach the engine your taste</p>
      <p className="mb-6 text-sm leading-relaxed text-stone-500">
        Save quotes you love in the Library and Discover will surface more just like them.
      </p>
      <button
        onClick={() => navigate("/library")}
        className="rounded-2xl bg-stone-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
      >
        Browse the Library →
      </button>
    </div>
  );
}

type CardQuote = ReturnType<typeof toCardQuote>;

export function QuoteGridPage({
  activeNav,
  searchQuery = "",
}: {
  activeNav: string;
  searchQuery?: string;
}) {
  const [, navigate] = useLocation();
  const authed = useAuthEnabled();

  // Helper to update URL search params, keeping all other params intact
  function updateUrlParams(updates: Record<string, string | null>) {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const qs = params.toString();
    navigate(window.location.pathname + (qs ? `?${qs}` : ""), { replace: true });
  }

  // Tag filter — persisted in URL as ?tag=<name>
  const [activeTag, setActiveTag] = useState<string | null>(() => {
    return new URLSearchParams(window.location.search).get("tag");
  });

  function handleTagChange(tag: string | null) {
    setActiveTag(tag);
    updateUrlParams({ tag: tag });
  }

  // Language filter — persisted in URL as ?language=english|non-english
  const [activeLang, setActiveLang] = useState<"" | "english" | "non-english">(() => {
    const p = new URLSearchParams(window.location.search).get("language");
    return p === "english" || p === "non-english" ? p : "";
  });

  function handleLangChange(lang: "" | "english" | "non-english") {
    setActiveLang(lang);
    updateUrlParams({ language: lang || null });
  }

  function clearAllFilters() {
    setActiveTag(null);
    setActiveLang("");
    updateUrlParams({ tag: null, language: null });
  }

  // Debounced search term — used for server-side Library filtering to avoid a
  // request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Library pagination state
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryAllQuotes, setLibraryAllQuotes] = useState<CardQuote[]>([]);

  // Fresh shuffle seed per visit: generated once when this view mounts so the
  // Library appears in a randomized (author-unclustered) order that stays stable
  // across "Load More" pages. A new visit (remount) rolls a new seed.
  const [shuffleSeed] = useState(() => Math.floor(Math.random() * 2_147_483_647));

  // Track the filter key we last reset for — enables synchronous reset on
  // tag/search/language changes, avoiding stale renders and wasted fetches.
  const filterKey = `${activeTag ?? ""}|${debouncedSearch}|${activeLang}`;
  const [trackedFilterKey, setTrackedFilterKey] = useState(filterKey);

  if (trackedFilterKey !== filterKey) {
    setTrackedFilterKey(filterKey);
    setLibraryPage(1);
    setLibraryAllQuotes([]);
  }

  // Tag chip expand state
  const [tagsExpanded, setTagsExpanded] = useState(false);

  // Fetch all tags with quote counts; sorted by count desc for top-10 slicing
  const tagsQuery = useGetTags({
    query: { queryKey: getGetTagsQueryKey() },
  });
  const allTags = useMemo(() => {
    const tags = tagsQuery.data ?? [];
    return [...tags].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [tagsQuery.data]);
  const visibleTags = useMemo(() => {
    if (tagsExpanded) return allTags;
    const top = allTags.slice(0, TOP_TAG_COUNT);
    if (activeTag && !top.some((t) => t.name === activeTag)) {
      const activeTagObj = allTags.find((t) => t.name === activeTag);
      if (activeTagObj) return [...top, activeTagObj];
    }
    return top;
  }, [tagsExpanded, allTags, activeTag]);
  const hasMoreTags = allTags.length > TOP_TAG_COUNT;

  const recParams = { limit: 20 };
  const recommendationsQuery = useGetQuotesRecommendations(recParams, {
    query: {
      queryKey: getGetQuotesRecommendationsQueryKey(recParams),
      enabled: authed && activeNav === "Discover",
    },
  });

  const clustersQuery = useGetProfileQuoteClusters({
    query: {
      queryKey: getGetProfileQuoteClustersQueryKey(),
      enabled: authed && activeNav === "Discover",
    },
  });

  const favoritesQuery = useGetQuotesFavorites({
    query: {
      queryKey: getGetQuotesFavoritesQueryKey(),
      enabled: authed && activeNav === "Favorites",
    },
  });

  // Library: server-side text search via debounced `query` param + pagination
  const libraryParams = {
    page: libraryPage,
    pageSize: LIBRARY_PAGE_SIZE,
    status: "approved" as const,
    seed: shuffleSeed,
    ...(activeTag ? { tag: activeTag } : {}),
    ...(debouncedSearch.trim() ? { query: debouncedSearch.trim() } : {}),
    ...(activeLang ? { language: activeLang } : {}),
  };
  const libraryQuery = useGetQuotes(libraryParams, {
    query: {
      queryKey: getGetQuotesQueryKey(libraryParams),
      enabled: activeNav === "Library",
    },
  });

  // Accumulate Library results across pages
  const prevLibraryData = useRef<typeof libraryQuery.data>(undefined);
  useEffect(() => {
    if (!libraryQuery.data || libraryQuery.data === prevLibraryData.current) return;
    prevLibraryData.current = libraryQuery.data;
    const newQuotes = libraryQuery.data.data.map(toCardQuote);
    if (libraryQuery.data.page === 1) {
      setLibraryAllQuotes(newQuotes);
    } else {
      setLibraryAllQuotes((prev) => [...prev, ...newQuotes]);
    }
  }, [libraryQuery.data]);

  const libraryTotal = libraryQuery.data?.total ?? 0;
  const libraryHasMore = libraryAllQuotes.length < libraryTotal;

  const activeError =
    activeNav === "Discover" ? recommendationsQuery.error :
    activeNav === "Favorites" ? favoritesQuery.error :
    libraryQuery.error;

  if (isAuthError(activeError)) {
    return <AuthErrorPrompt />;
  }

  // Signed-out short-circuit for tabs that require auth — render the
  // sign-in nudge instead of empty loading states.
  if (!authed && (activeNav === "Favorites" || activeNav === "Discover")) {
    return <AuthErrorPrompt />;
  }

  const isLoading =
    (activeNav === "Discover" && recommendationsQuery.isLoading) ||
    (activeNav === "Favorites" && favoritesQuery.isLoading) ||
    (activeNav === "Library" && libraryQuery.isLoading && libraryPage === 1);

  const discoverQuotes = useMemo(
    () => (recommendationsQuery.data ?? []).map(toDiscoverCardQuote),
    [recommendationsQuery.data],
  );

  // Client-side filtering for Favorites and Discover
  const filteredFavorites = useMemo(() => {
    const all = (favoritesQuery.data ?? []).map(toCardQuote);
    let filtered = activeTag ? all.filter((q) => q.tags.includes(activeTag)) : all;
    if (searchQuery.trim()) filtered = filtered.filter((q) => matchesSearch(q, searchQuery));
    return filtered;
  }, [favoritesQuery.data, searchQuery, activeTag]);

  const filteredDiscover = useMemo(() => {
    if (!searchQuery.trim()) return discoverQuotes;
    return discoverQuotes.filter((q) => matchesSearch(q, searchQuery));
  }, [discoverQuotes, searchQuery]);

  const visibleQuotes = useMemo(() => {
    if (activeNav === "Discover") return filteredDiscover;
    if (activeNav === "Favorites") return filteredFavorites;
    if (activeNav === "Library") return libraryAllQuotes;
    return [];
  }, [activeNav, filteredDiscover, filteredFavorites, libraryAllQuotes]);

  const clusters = clustersQuery.data ?? [];

  return (
    <section data-testid={`page-${activeNav.toLowerCase()}`}>
      {/* Taste snapshot — only on Discover, only when there are saved quotes */}
      {activeNav === "Discover" && clusters.length > 0 && (
        <TasteSnapshot clusters={clusters} />
      )}

      {/* Filters bar — not shown on Discover (curated feed) */}
      {activeNav !== "Discover" && (
        <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-stone-700">Filters</span>
            {activeNav === "Library" && (
              <select
                value={activeLang}
                onChange={(e) => handleLangChange(e.target.value as "" | "english" | "non-english")}
                data-testid="filter-language"
                className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-700 transition focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-1 hover:bg-stone-100"
              >
                <option value="">All languages</option>
                <option value="english">English only</option>
                <option value="non-english">Non-English only</option>
              </select>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {visibleTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagChange(activeTag === tag.name ? null : tag.name)}
                className="active:scale-[0.97] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 rounded-full"
                data-testid={`filter-${tag.name}`}
              >
                <TagChip active={activeTag === tag.name}>{tag.name}</TagChip>
              </button>
            ))}
          </div>
          {hasMoreTags && (
            <button
              onClick={() => setTagsExpanded((e) => !e)}
              className="mt-2 text-xs font-medium text-stone-500 hover:text-stone-800 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 rounded"
            >
              {tagsExpanded ? "Show less" : `Show all ${allTags.length}`}
            </button>
          )}
        </div>
      )}

      {/* Active search label */}
      {searchQuery.trim() && (
        <p className="mb-3 text-sm text-stone-500">
          Searching for{" "}
          <span className="font-medium text-stone-700">"{searchQuery}"</span>
        </p>
      )}

      {/* Library result count */}
      {activeNav === "Library" && !isLoading && visibleQuotes.length > 0 && (
        <p className="mb-3 text-sm text-stone-500">
          Showing{" "}
          <span className="font-medium text-stone-700">{visibleQuotes.length}</span>
          {libraryTotal > visibleQuotes.length ? (
            <> of <span className="font-medium text-stone-700">{libraryTotal}</span></>
          ) : null}{" "}
          quote{libraryTotal !== 1 ? "s" : ""}
          {activeTag ? (
            <> tagged <span className="font-medium text-stone-700">"{activeTag}"</span></>
          ) : null}
        </p>
      )}

      {isLoading ? (
        activeNav === "Discover" && !searchQuery.trim() ? (
          <DiscoverLoadingState />
        ) : (
          <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
            <p className="animate-pulse text-sm text-stone-400">Loading quotes…</p>
          </div>
        )
      ) : activeNav === "Discover" && visibleQuotes.length === 0 && !searchQuery.trim() ? (
        <DiscoverEmptyState />
      ) : visibleQuotes.length === 0 ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">
            {searchQuery.trim()
              ? `No results for "${searchQuery}".`
              : "No quotes match this filter."}
          </p>
          {(activeTag || activeLang || searchQuery.trim()) && (
            <button
              className="mt-4 text-sm font-medium text-stone-900 underline underline-offset-4 hover:text-stone-600 active:scale-[0.97] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 rounded"
              onClick={clearAllFilters}
            >
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {visibleQuotes.map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                initialFavorite={activeNav === "Favorites"}
                enableFlip
                onDismiss={activeNav === "Discover" ? (_id) => {} : undefined}
              />
            ))}
          </div>
          {activeNav === "Library" && libraryHasMore && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setLibraryPage((p) => p + 1)}
                disabled={libraryQuery.isFetching}
                className="rounded-2xl border border-stone-200 bg-white px-7 py-2.5 text-sm font-medium text-stone-700 shadow-sm transition hover:bg-stone-50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50"
              >
                {libraryQuery.isFetching ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
